/* eslint-disable no-console */
import {cloneDeep, delay} from 'lodash';
import axios from 'axios';
import qs from 'query-string';
import env from '../util/env';
import DocHubClient from '../util/doc-hub-client';
import {currentResource, labels} from '../util/utils';
import {serviceURL, HttpStatusCodes, relateDocumentsTo, documentTabLayoutTabs, clientSideEventNames, documentTypes, formBodyType} from '../util/uiconstants';
import {ADD_DUPLICATE_DOCUMENT} from '../actions/actiontypes';
import * as DocumentActions from '../actions/Document/documentactions';
import * as DocumentExtensionActions from '../actions/Document/documentextensionactions';
import * as RelatedDocumentsActions from '../actions/RelatedDocuments/relatedDocumentsActions';
import {getUnresolvedCommentsCount} from '../actions/ValidationControl/validationcontrolactions';
import {clearErrors} from '../actions/erroractions';
import {UpdateInstruction} from '../actions/GroupAudit/GroupInstructions';
import {dispatchUploadInfoEvent} from '../util/uploadEventUtils';
import {checkIsMFE} from '../util/customHooks';
import {clearUploadDocuments, saveUploadDocumentId, saveUploadDocuments, updateUploadDocument} from '../actions/Document/bulkRelateActions';
import {getRelatedDocument} from '../actions/FormBodyRelatedDocuments/formbodyrelateddocumentsactions';

const checkListBodyTypes = [
	formBodyType.DocumentUploadPICApprovalRequired,
	formBodyType.DocumentUploadEQRApprovalRequired,
	formBodyType.DocumentUploadPICEQRApprovalRequired,
	formBodyType.DocumentUploadAnyApprovalRequired
];

/**
 * DocHelper Middleware
 * This will handle the api calls to download document
 * Will set the docHelper online state to true
 * @param {Object} config - config.
 * @returns {Object} next action.
 */
const docHelperMiddleware = (config) => (store) => (next) => (action) => {
	const API_ERRORS = 'API_ERRORS';
	const registeredDownloadConnections = new WeakSet();

	// Middleware should be executed only for docHelper calls
	if (!(action.meta && action.meta.docHelper)) {
		return next(action);
	}

	/**
	 * Dispatches required actions to save the reason for change + rationale text for documents uploaded after the engagement report release date.
	 * @param {Object} docData document object.
	 */
	const saveDocumentChangeReason = function (docData) {
		if (docData.isUploadAfterRRD) {
			const engagementId = docData.engagementId;
			const documentId = docData.documentId;

			const updateDocumentAction = DocumentActions.updateDocument(engagementId, documentId, {
				documentChangeTypeId: docData.documentChangeTypeId
			});

			let getDocumentExtensionsAction = DocumentExtensionActions.getAllDocumentExtensions(engagementId, documentId);

			// 1. Search for documentExtension data for the uploaded document
			// 2.1. If nothing was found, send a createDocExtension action
			// 2.2. If there is data for that docId, send a updateDocExtension action
			store.dispatch(getDocumentExtensionsAction).then(() => {
				let docExtensions = store.getState().documentExtensions;
				if (docExtensions && Array.isArray(docExtensions)) {
					let documentExtension = docExtensions.find((de) => de.documentId === documentId);

					let saveChangeCommentAction;

					if (documentExtension) {
						saveChangeCommentAction = DocumentExtensionActions.updateDocumentChangeComment(engagementId, documentId, documentExtension.id, {
							changeReason: docData.rationaleText
						});
					} else {
						saveChangeCommentAction = DocumentExtensionActions.createDocumentChangeComment(engagementId, documentId, {
							changeReason: docData.rationaleText
						});
					}

					store.dispatch(updateDocumentAction).then(() => store.dispatch(saveChangeCommentAction));
				}
			});
		}
	};

	/**
	 * Dispatches required actions to save the document to document related object.
	 * @param {Object} docData: document object.
	 * @param {Number} entityUid: bodyId.
	 * @param {Boolean} skipDocumentCountUpdate: skipDocumentCountUpdate.
	 */
	const saveDocumentRelatedObject = function (docData, entityUid, skipDocumentCountUpdate = false) {
		const relatedDocuments = {RelatedDocumentId: docData.documentId};

		const documentId = store.getState().documentId;

		const relatedDocumentsAction = RelatedDocumentsActions.createRelatedDocument(
			docData.engagementId,
			documentId,
			entityUid,
			relatedDocuments,
			relateDocumentsTo.canvasFormBody,
			docData.parentName,
			docData.sectionId
		);

		store.dispatch({...relatedDocumentsAction, skipDocumentCountUpdate}).then(() => {
			if (docData.requireValidations) {
				store.dispatch(getUnresolvedCommentsCount(docData.engagementId, documentId, docData.documentType));
			}

			return store.dispatch(getRelatedDocument(docData.engagementId, docData.documentId, entityUid));
		});
	};

	const taskDocumentAssociation = async (engagementId, documentId, taskIds = [], entityUId, documentType) => {
		if (taskIds.length === 0) {
			return Promise.resolve();
		}

		const url = env.getBaseURL(serviceURL, engagementId) + 'TaskDocuments';

		const data = {
			documentId,
			targetDocumentTypeId: documentType,
			taskIds
		};

		return await store.dispatch({
			type: 'ASSOCIATE_TASK_DOCUMENTS',
			meta: {
				api: {
					url,
					method: 'POST',
					data
				}
			},
			entityUId
		});
	};

	const clearDocHelperDoc = (fileName) => {
		store.dispatch({
			type: 'KRONOS_DOCHELPER_UPLOAD_COMPLETE',
			meta: {
				fileName
			}
		});
	};

	const associateTasks = async (engagementId, documentId, relatedTasks = [], entityUId, documentType) => {
		const taskIds = [];

		// This is to handle the case where relatedTasks is a single taskId
		// (To drawer on GroupInstructions ("Instructions and reporting forms") )
		if (!Array.isArray(relatedTasks)) {
			relatedTasks = [{taskId: relatedTasks}];
		}

		if (relatedTasks.length > 0) {
			const ids = relatedTasks.map((task) => task.taskId);

			taskIds.push(...ids);
		} else {
			let taskId = qs.parse(window.location.search.toLowerCase()).taskid;

			if (!taskId) {
				const aresTaskDocuments = store.getState().aresTaskDocuments;

				if (aresTaskDocuments?.documents?.byId) {
					taskId = Object.values(aresTaskDocuments.documents.byId)[0].taskId;
				}
			}

			if (taskId) {
				taskIds.push(taskId);
			}
		}

		return await taskDocumentAssociation(engagementId, documentId, taskIds, entityUId, documentType);
	};

	const uploadComplete = async function (message) {
		console.log('📃: Upload Complete ✔', message);

		const state = store.getState();

		const engagementId = Number(state.engagement.engagementId);

		if (engagementId !== Number(message.EngagementId)) {
			return;
		}

		store.dispatch(saveUploadDocumentId(message.FileId));
		store.dispatch(updateUploadDocument({name: message.FileName, uploadComplete: true}));

		const act = cloneDeep(action);

		const docData = cloneDeep(state.docHelperDocuments[message.FileName] || {});

		if (Object.keys(docData).length === 0) {
			console.log('📃: No document data found for', message.FileName);
		}

		docData.documentId = message.FileId;
		docData.engagementId = engagementId;
		docData.entityUId = docData.entityUid || act.meta.docHelper.payload.entityUid;
		docData.filename = docData.filename || message.FileName;
		docData.documentName = docData.documentName || message.FileName.split('.')[0];
		docData.fileExt = docData.fileExt || message.FileName.split('.')[1];
		docData.parentName = docData.parentName || act.meta.docHelper.payload.parentName;
		docData.sectionId = docData.sectionId || act.meta.docHelper.payload.sectionId;
		docData.documentType = docData.documentType || act.meta.docHelper.payload.documentType;
		docData.requireValidations = docData.requireValidations || act.meta.docHelper.payload.requireValidations;
		docData.signoffRequirements = docData.signoffRequirements || act.meta.docHelper.payload.signoffRequirements;

		const isCheckListBody = checkListBodyTypes.includes(Number(docData.bodyTypeId));

		if (docData && act.meta.docHelper.data && act.meta.docHelper.data.length > 0 && docData.filePath) {
			if (!act.meta.docHelper.data[0].includes(docData.filePath) || act.meta.docHelper.data[0] === docData.filePath) {
				/*action doesnt have latest data*/
				act.meta.docHelper.payload.entityUid = docData.entityUid;
				act.meta.docHelper.payload.filename = docData.filename;
				act.meta.docHelper.payload.documentName = docData.documentName;
				act.meta.docHelper.payload.fileExt = docData.fileExt;
				act.meta.docHelper.payload.signoffRequirements = docData.signoffRequirements;
			}
		}

		const updatedAction = Object.assign({}, act, {
			type: 'KRONOS_DOCUMENT_UPLOAD_COMPLETE',
			modifiedDate: new Date(),
			lastModifiedFullName: `${state.userPreferences?.firstName || ''} ${state.userPreferences?.lastName || ''}`.trim(),
			meta: {
				docHelper: {
					payload: Object.assign({}, act.meta.docHelper.payload, {
						documentId: message.FileId,
						inProgress: false
					}),
					message: message
				}
			}
		});

		const isUserUpload = updatedAction.meta.docHelper?.payload.isUserUpload;

		if (updatedAction.meta.docHelper) {
			updatedAction.meta.docHelper.payload.isUserUpload = false;
		}

		const relatedTasks = action.relatedTasks;

		if (!isCheckListBody) {
			let documentTypeId = docData?.parentName !== documentTabLayoutTabs.temporaryName ? documentTypes.evidence : documentTypes.crash;

			await associateTasks(engagementId, message.FileId, relatedTasks, docData.entityUId, documentTypeId);

			if (docData.entityUId && docData?.parentName !== documentTabLayoutTabs.temporaryName) {
				saveDocumentRelatedObject(docData, docData.entityUId, action.showCrashDocumentWarningMessage);
			}
		}

		// if the document is uploaded in group audit section, it will create the relation instruction document
		if (Object.keys(state.groupAudit?.groupAuditDocumentUploadOn).length !== 0) {
			UpdateInstructionCall(store, docData.documentId);
		}

		if (act.meta.docHelper.payload?.signoffRequirements) {
			const payload = act.meta.docHelper.payload?.signoffRequirements;
			store.dispatch(
				DocumentActions.updateDocument(
					act.meta.docHelper.payload.engagementId,
					docData.documentId,
					payload,
					undefined,
					undefined,
					undefined,
					docData.entityUId,
					'FORM_BODY_QUICK_REFERENCE'
				)
			);
		}

		if (docData?.parentName === documentTabLayoutTabs.temporaryName) {
			store.dispatch({
				type: 'UPDATE_DOCUMENT_COUNT_ON_TEMPORARY_FILE_UPLOAD',
				entityUid: docData.entityUId,
				tabname: docData.parentName,
				sectionId: docData.sectionId
			});
		}

		if (isUserUpload) {
			saveDocumentChangeReason(docData);
		}

		clearDocHelperDoc(message.FileName);

		next(updatedAction);

		if (action.showCrashDocumentWarningMessage) {
			const isMFE = checkIsMFE();
			store.dispatch(clearErrors());

			// Dispatch custom event to be handled by FileUpload component
			// This will emit the message to the parent app via message hub when in MFE mode
			dispatchUploadInfoEvent(labels.noTaskAssociatedToastMessage, 0, isMFE, store.dispatch);

			store.dispatch({
				type: 'REMOVE_TASK_DOCUMENT',
				documentName: `${docData.documentName}.${docData.fileExt}`,
				entityUid: docData.entityUid,
				isOnline: true
			});
		}

		const currentState = store.getState();

		const uploadDocuments = currentState.bulkRelate.uploadDocuments;

		const pendingUploads = uploadDocuments.filter((doc) => !doc.uploadComplete);

		if (pendingUploads.length === 0) {
			store.dispatch(clearUploadDocuments());

			if (isCheckListBody) {
				window.dispatchEvent(
					new CustomEvent(clientSideEventNames.openRelateTaskModal, {
						detail: {
							documentId: message.FileId,
							isBulkRelate: true,
							bodyId: act.meta.docHelper.payload.entityUid,
							bodyTypeId: docData.bodyTypeId,
							sectionId: docData.sectionId,
							parentName: docData.parentName
						}
					})
				);
			}
		}
	};

	const UpdateInstructionCall = (store, documentId) => {
		const state = store.getState();

		const payload = {
			sectionId: state.groupAudit?.groupAuditDocumentUploadOn?.sectionId,
			Documents: [
				{
					id: documentId,
					checked: true
				}
			]
		};
		store.dispatch(
			UpdateInstruction(state.groupAudit?.groupAuditDocumentUploadOn?.engagementId, state.groupAudit?.groupAuditDocumentUploadOn?.instructionId, payload)
		);
	};

	/**
	 * This is a SignalR hub client method, which is triggered when there is an upload Error
	 * @param {Array} message - Error object passed from DocHelper has a unique format of Array of strings
	 * @param {String} Id - id
	 * @returns {Object} an action
	 */
	const uploadError = function (message, Id) {
		console.log('📃: Upload Error ❌', message, Id);
		return next({
			type: 'API_ERROR',
			error: message
		});
	};

	/**
	 * This is the SignalR hub client method, which is called on regular intervals during upload Progress
	 * @param {Object} message - contains details of Progress for the document being uploaded
	 * Here we dispatch the Progress action to handle the progress indicator with redux store update
	 * @returns {Object} the next action.
	 */
	const uploadProgress = function (message) {
		console.log('📃: Upload Progress ⚡⚡⚡⚡⚡', message);
		let updatedAction = Object.assign({}, action, {
			type: 'KRONOS_DOCUMENT_UPLOAD_PROGRESS',
			meta: {
				docHelper: {
					payload: Object.assign({}, store.getState().docHelperDocuments[message.FileName], {
						documentId: message.FileId,
						inProgress: message.ProgressPercent !== 100,
						progressPercent: message.ProgressPercent
					}),
					message: message
				}
			}
		});
		return next(updatedAction);
	};

	const addDocumentError = function (error) {
		const args = Array.prototype.slice.call(arguments);

		console.log('📃❌❌❌❌❌add Document Error: ', args);

		if (args[0] === HttpStatusCodes.PreConditionFailed) {
			const doc = store.getState().docHelperDocuments[args[1]];

			return next({
				type: ADD_DUPLICATE_DOCUMENT,
				meta: {
					document: {
						documentName: args[1],
						documentId: args[4],
						docTypeId: args[5],
						engagementId: args[6],
						payload: doc,
						data: [doc?.filePath]
					}
				}
			});
		} else {
			// Adding timeout as because dochelper retrurn 200 for API Error
			setTimeout(() => {
				next({
					type: 'REMOVE_TASK_DOCUMENT',
					meta: action.meta,
					documentName: args[1],
					entityUid: store.getState().docHelperDocuments[args[1]]?.entityUid,
					isOnline: true
				});
			}, 3000);

			return next({
				type: 'DOCHELPER_ERROR',
				error: args
			});
		}
	};

	const downloadProgress = function (message, inProgress) {
		console.log('⚡⚡⚡⚡⚡📃: download Progress', message.ProgressPercent);
		//turnOffDownloadListeners();
		let updatedAction = Object.assign({}, action, {
			type: 'KRONOS_DOCUMENT_DOWNLOAD_PROGRESS',
			meta: {
				docHelper: {
					payload: Object.assign({}, action.meta.docHelper.payload, {
						inProgress: inProgress ? inProgress : message.ProgressPercent !== 100,
						progressPercent: message.ProgressPercent
					}),
					message: message
				}
			}
		});
		return next(updatedAction);
	};

	const downloadComplete = function (message) {
		console.log('✔📃: Download Complete', message, 'action:', action);
		//turnOffDownloadListeners();
		delay(
			function () {
				//Make some delay to hide progress bar and to show progress because some time download progress evet is not trigered by doc helper
				// if the file is already downloaded on user machine or file size is very small
				let updatedAction = Object.assign({}, action, {
					type: 'KRONOS_DOCUMENT_DOWNLOAD_COMPLETE',
					meta: {
						docHelper: {
							payload: Object.assign({}, action.meta.docHelper.payload, {
								inProgress: false
							}),
							message: message
						}
					}
				});
				return next(updatedAction);
			},
			1000,
			message
		);

		// complete is triggered in delay
		downloadProgress(message, true);
	};

	const downloadCanceled = function (message) {
		console.log('❌📃: download Canceled', message);

		const canceledAction = Object.assign({}, action, {
			type: API_ERRORS,
			error: 'downloadCancelled',
			message: message,
			meta: {
				docHelper: {
					message: message,
					payload: Object.assign({}, action.meta.docHelper.payload, {
						documentId: action.meta.docHelper.payload.documentId,
						fileName: action.meta.docHelper.payload.documentName,
						engagementId: action.meta.docHelper.payload.engagementId,
						inProgress: false
					})
				}
			}
		});

		return next(canceledAction);
	};

	//Check for config availability
	if (!config) {
		const configMsg = `
        Config unavailable.
        The AD package requires config to get the authorization token for the api call.`;
		console.log(configMsg);
	}

	const connection = DocHubClient.getConnection();

	if (action.type === 'KRONOS_DOCHELPER_DOWNLOAD_DOCUMENT') {
		if (!registeredDownloadConnections.has(connection)) {
			// Registering documentHub events with Listeners
			connection.on('downloadComplete', downloadComplete);
			connection.on('downloadProgress', downloadProgress);
			connection.on('downloadCanceled', downloadCanceled);
			connection.on('downloadError', (msg, e) => {
				console.log('📃❌❌❌❌❌Download Error: ', msg, e);
			});
			registeredDownloadConnections.add(connection);
		}
	}

	// For DocHelper Upload we need to create an array for paths
	if (action.type === 'KRONOS_DOCHELPER_UPLOAD_DOCUMENT') {
		connection.off('uploadProgress');
		connection.off('uploadComplete');
		connection.off('uploadError');
		connection.off('addDocumentError');

		connection.on('uploadProgress', uploadProgress);
		connection.on('uploadComplete', uploadComplete);
		connection.on('uploadError', uploadError);
		connection.on('addDocumentError', addDocumentError);

		if (action.response) {
			action.meta.docHelper.data = action.response.map((item) => {
				return item.filePath;
			});
		} else {
			console.log('action.response is null', action);
		}
	}

	const setDocHelperState = () => {
		return axios({
			method: 'PUT',
			url: `${action.docHelperConfig.docHelperApiUri}onlinestate?state=true&docUrl=${env.getURL('Engg_Doc_Url')}&resourceUri=${env.getURL(
				'Engg_AuthAudience_URL'
			)}`
		});
	};

	const associateDocuments = () => {
		const {method, url, data, headers} = action.meta.docHelper;

		if (Array.isArray(action.response)) {
			const result = action.response.map((doc) => {
				return {name: doc.name, uploadComplete: false};
			});

			if (result.length > 0) {
				store.dispatch(saveUploadDocuments(result));
			}
		}

		if (Array.isArray(action.response) && action.response.length > 0) {
			const payload = action.response.filter((res) => {
				return data.includes(res.filePath);
			});

			action.meta.docHelper.docPayload = payload.map((f) => {
				return Object.assign({}, action.meta.docHelper.payload, {
					key: f.name,
					id: f.name,
					filename: f.name,
					documentName: f.name.split(f.fileExtension)[0],
					fileExt: f.fileExtension.split('.')[1],
					size: f.size,
					filePath: f.filePath
				});
			});

			if (action.type === 'KRONOS_DOCHELPER_UPLOAD_DOCUMENT' && !action.overwrite) {
				next(action);
			}
		}

		return axios({method, url, data, headers});
	};

	setDocHelperState()
		.then(associateDocuments)
		.then((response) => {
			action.response = response.data;

			if (action.type !== 'KRONOS_DOCHELPER_UPLOAD_DOCUMENT') {
				return next(action);
			}
		})
		.catch((error) => {
			action.error = error;
			action.response = {};
			action.responseObj = {};
			return next({type: API_ERRORS, error});
		});
};

export default docHelperMiddleware;
